import { useState } from "react";
import axios from "axios";
import { Phone, Send, Loader2, CheckCircle2 } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/config/site";
import { formatApiErrorDetail } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact above-the-fold quote-request form for the hero panel.
 * 3 fields: phone (required), city, vehicle. POSTs to /api/contact.
 */
export default function HeroQuoteForm() {
  const [f, setF] = useState({ phone: "", city: "", vehicle: "" });
  const [s, setS] = useState({ loading: false, ok: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.phone.trim()) return;
    setS({ loading: true, ok: false, error: "" });
    try {
      await axios.post(`${API}/contact`, {
        name: "Hero Quote Request",
        email: "quote-inbox@bmtowingrecovery.com",
        phone: f.phone.trim(),
        service: "Quote Request",
        subject: "New quote request from homepage hero",
        message: `Fast quote request from the homepage.\n\nPhone: ${f.phone.trim()}\nCity: ${f.city.trim() || "(not specified)"}\nVehicle: ${f.vehicle.trim() || "(not specified)"}\n\nPlease call the customer back.`,
      });
      setS({ loading: false, ok: true, error: "" });
      setF({ phone: "", city: "", vehicle: "" });
    } catch (err) {
      setS({
        loading: false,
        ok: false,
        error: formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't send. Please call us.",
      });
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="hero-quote-form"
      className="lux-glass-strong rounded-2xl p-5 sm:p-6"
      noValidate
    >
      <div className="font-display uppercase tracking-[0.28em] text-yellow-400 text-[11px] flex items-center gap-2">
        <span className="inline-block h-1.5 w-6 bg-yellow-400 rounded-full" />
        Fast Quote · 30 seconds
      </div>
      <div className="mt-2 font-display font-black uppercase text-white text-xl sm:text-2xl leading-tight">
        We'll call you back.
      </div>

      <label className="block mt-4">
        <span className="font-display uppercase tracking-[0.18em] text-[10px] text-zinc-400">Your phone *</span>
        <input
          type="tel"
          required
          value={f.phone}
          onChange={(e) => setF((v) => ({ ...v, phone: e.target.value }))}
          data-testid="hero-quote-phone"
          placeholder="(425) 555-0123"
          className="mt-1 w-full bg-black/50 border border-white/10 focus:border-yellow-400/70 rounded-lg text-white placeholder:text-zinc-500 px-3.5 py-3 text-sm outline-none transition-colors"
        />
      </label>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <input
          type="text"
          value={f.city}
          onChange={(e) => setF((v) => ({ ...v, city: e.target.value }))}
          data-testid="hero-quote-city"
          placeholder="Your city"
          className="w-full bg-black/50 border border-white/10 focus:border-yellow-400/70 rounded-lg text-white placeholder:text-zinc-500 px-3.5 py-3 text-sm outline-none transition-colors"
        />
        <input
          type="text"
          value={f.vehicle}
          onChange={(e) => setF((v) => ({ ...v, vehicle: e.target.value }))}
          data-testid="hero-quote-vehicle"
          placeholder="Your vehicle"
          className="w-full bg-black/50 border border-white/10 focus:border-yellow-400/70 rounded-lg text-white placeholder:text-zinc-500 px-3.5 py-3 text-sm outline-none transition-colors"
        />
      </div>

      <button
        type="submit"
        disabled={s.loading || !f.phone.trim()}
        data-testid="hero-quote-submit"
        className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-black font-display font-black uppercase tracking-wider px-4 py-3.5 rounded-full lux-glow-yellow min-h-[52px] transition-colors"
      >
        {s.loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Send className="h-4 w-4" strokeWidth={3} /> Get My Quote</>}
      </button>

      <a
        href={`tel:${PHONE_TEL}`}
        data-testid="hero-quote-call"
        aria-label={`Call BM Towing 24/7 at ${PHONE_DISPLAY}`}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 text-yellow-400 hover:text-yellow-300 font-display uppercase tracking-[0.2em] text-[11px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300"
      >
        <Phone className="h-3 w-3" strokeWidth={3} aria-hidden="true" /> Or call {PHONE_DISPLAY}
      </a>

      {s.ok && (
        <div data-testid="hero-quote-success" role="status" aria-live="polite" className="mt-3 flex items-start gap-2 border border-green-500/40 bg-green-500/10 text-green-200 p-3 text-xs rounded-lg">
          <CheckCircle2 className="h-4 w-4 mt-0.5" aria-hidden="true" /> Got it — we'll call you back shortly. For anything urgent, please call.
        </div>
      )}
      {s.error && (
        <div data-testid="hero-quote-error" role="alert" aria-live="assertive" className="mt-3 border border-red-500/40 bg-red-500/10 text-red-200 p-3 text-xs rounded-lg">{s.error}</div>
      )}
    </form>
  );
}
